import { apiClient } from './api';
import type { Skill, SkillVersion, SkillAgent, SkillExtractionJob, SkillExtractionCandidate, ApiResponse } from '@/types';

class SkillsService {
  async getSkills(): Promise<Skill[]> {
    const response = await apiClient.get<ApiResponse<Skill[]>>('/skills');
    return response.data.data || [];
  }

  async getSkill(id: string): Promise<Skill> {
    const response = await apiClient.get<ApiResponse<Skill>>(`/skills/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Skill not found');
  }

  async createSkill(data: {
    name: string;
    description?: string;
    skill_type: 'common' | 'topic';
    rules: string[];
    few_shot_examples: Array<{ customer: string; agent: string }>;
  }): Promise<Skill> {
    const response = await apiClient.post<ApiResponse<Skill>>('/skills', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create skill');
  }

  async updateSkill(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      skill_type: string;
      status: string;
      rules: string[];
      few_shot_examples: Array<{ customer: string; agent: string }>;
    }>
  ): Promise<Skill> {
    const response = await apiClient.patch<ApiResponse<Skill>>(`/skills/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update skill');
  }

  async deleteSkill(id: string): Promise<void> {
    await apiClient.delete(`/skills/${id}`);
  }

  async extractSkills(sessionIds: string[]): Promise<Skill[]> {
    const response = await apiClient.post<ApiResponse<Skill[]>>('/skills/extract', {
      session_ids: sessionIds,
    });
    return response.data.data || [];
  }

  async getVersions(skillId: string): Promise<SkillVersion[]> {
    const response = await apiClient.get<ApiResponse<SkillVersion[]>>(
      `/skills/${skillId}/versions`
    );
    return response.data.data || [];
  }

  async linkAgents(skillId: string, agentIds: string[]): Promise<SkillAgent[]> {
    const response = await apiClient.post<ApiResponse<SkillAgent[]>>(
      `/skills/${skillId}/agents`,
      { agent_ids: agentIds }
    );
    return response.data.data || [];
  }

  async unlinkAgent(skillId: string, agentId: string): Promise<void> {
    await apiClient.delete(`/skills/${skillId}/agents/${agentId}`);
  }

  async getAgentSkills(agentId: string): Promise<Skill[]> {
    const response = await apiClient.get<ApiResponse<Skill[]>>(`/agents/${agentId}/skills`);
    return response.data.data || [];
  }

  async setVersion(skillId: string, version: number): Promise<void> {
    await apiClient.post(`/skills/${skillId}/set-version`, { version });
  }

  async getExtractionJob(jobId: string): Promise<{ job: SkillExtractionJob; candidates: SkillExtractionCandidate[] }> {
    const response = await apiClient.get<ApiResponse<{ job: SkillExtractionJob; candidates: SkillExtractionCandidate[] }>>(`/skills/extraction-jobs/${jobId}`);
    if (response.data.data) return response.data.data;
    throw new Error('Extraction job not found');
  }

  async mergeCandidate(candidateId: string, targetSkillId: string): Promise<void> {
    await apiClient.post(`/skills/extraction-candidates/${candidateId}/merge`, { target_skill_id: targetSkillId });
  }

  async createFromCandidate(candidateId: string): Promise<void> {
    await apiClient.post(`/skills/extraction-candidates/${candidateId}/create`);
  }

  async dismissCandidate(candidateId: string): Promise<void> {
    await apiClient.post(`/skills/extraction-candidates/${candidateId}/dismiss`);
  }
}

export const skillsService = new SkillsService();
